import { useCustomViewBlockContext } from '@lighthouse/block'
import type { Conditions, FieldCellValue, FilterFormType, RelativeSelectConfig } from '@lighthouse/core'
import { AnyObject, type FieldBlockAbstract } from '@lighthouse/core'
import { CURRENT_USER, isNumberValue, isStringArray, useAtomData, useFormModuleContext, useTimezoneOffset } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import { useSetAtom } from 'jotai'
import { reduce } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useLatest, useMount, useUpdateEffect } from 'react-use'

import { pageStackOfBlockAtom } from '@/atoms/application/state'
import { addRecordsAtom } from '@/atoms/dataSource/action'
import { useCurrentStackIdContext } from '@/contexts/PageContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useFieldBlockDefaultValueRender } from '@/hooks/useFieldBlockDefaultValueRender'
import * as srv from '@/services'

import FieldInputController from './FieldInputController'

interface FieldBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: FieldBlockAbstract
}

const FieldBlockController: React.FC<FieldBlockControllerProps> = ({ blockData }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { config, id: blockId } = blockData
    const { inputType, fieldPointer = '', initialValue } = config
    const formModule = useFormModuleContext()
    const stackId = useCurrentStackIdContext()
    const fieldBlockValueMap = useAtomData(
        pageStackOfBlockAtom,
        useCallback(s => s?.[stackId], [stackId])
    )
    const addRecords = useSetAtom(addRecordsAtom)
    const { record: viewRecord } = useCustomViewBlockContext()
    const { dataSource, record: formRecord, pointer: formPointer, formResetKey } = formModule ?? {}
    const record = formRecord ?? viewRecord
    const defaultValueRender = useFieldBlockDefaultValueRender(initialValue, record)
    const tzOffset = useTimezoneOffset()
    const [defaultValue, setDefaultValue] = useState<FieldCellValue | undefined>(undefined)

    const defaultValueRef = useLatest(defaultValue)

    const field = useMemo(() => dataSource?.schema?.[fieldPointer], [dataSource?.schema, fieldPointer])

    const handleGetInitRecord = useCallback(
        async (relativeSelect: RelativeSelectConfig['relativeSelect'], defaultValue: FieldCellValue | undefined) => {
            const { relativePointer, relativeFieldPointer } = relativeSelect ?? {}
            const initCondition =
                defaultValue === '' || defaultValue === undefined
                    ? []
                    : (isNumberValue(defaultValue) || isStringArray(defaultValue) ? defaultValue.toString() : '')
                          ?.split(',')
                          .filter(item => item !== CURRENT_USER.id) ?? []
            const initExpression: Conditions = reduce<string, AnyObject[]>(
                (preVal, curVal) => {
                    if (curVal === '') {
                        return preVal
                    }
                    const condition = {
                        idVariable: {
                            type: 'FIELD_ID',
                            fieldIdVariable: {
                                fieldId: relativeFieldPointer
                            }
                        },
                        operator: '=',
                        paramList: [
                            {
                                type: 'VALUE',
                                valueVariable: {
                                    value: curVal
                                }
                            }
                        ]
                    }
                    return [...preVal, condition]
                },
                [],
                initCondition
            )
            if (initExpression.length === 0) {
                return
            }
            const initFilter: FilterFormType = {
                expression: {
                    conditions: initExpression,
                    where: 'OR'
                }
            }
            if (relativePointer) {
                // const { id, pagination, search, dsId, filter, sorts } = arg
                const data = await srv.getDs({
                    appId,
                    envId,
                    dsId: relativePointer,
                    pagination: { currentPage: 1, pageSize: 50 },
                    filter: initFilter
                })

                if (data) {
                    addRecords({ records: data.records || [] })
                }
                return data
            }
        },
        [addRecords, appId, envId]
    )

    const getDefaultInputValue = useCallback(async () => {
        const res = await defaultValueRender(
            {
                id: blockId,
                dsId: formPointer ?? dataSource?.id ?? '',
                inputType,
                fieldId: fieldPointer,
                initialValue,
                config
            },
            fieldBlockValueMap
        )
        const defaultValue = res.value
        // eslint-disable-next-line unicorn/consistent-destructuring
        if (config.inputType === 'relativeSelect' && field?.type !== 'select') {
            // eslint-disable-next-line unicorn/consistent-destructuring
            await handleGetInitRecord(config.relativeSelect, defaultValue)
        }
        if (!isDeepEqual(defaultValueRef.current, defaultValue)) {
            setDefaultValue(defaultValue)
        }
    }, [
        defaultValueRender,
        blockId,
        formPointer,
        dataSource?.id,
        inputType,
        fieldPointer,
        initialValue,
        config,
        fieldBlockValueMap,
        field?.type,
        defaultValueRef,
        handleGetInitRecord
    ])

    useMount(() => {
        getDefaultInputValue()
    })

    useUpdateEffect(() => {
        getDefaultInputValue()
    }, [initialValue, field, record, formResetKey, defaultValueRender, tzOffset])
    return <FieldInputController blockData={blockData} defaultValue={defaultValue} />
}

export default FieldBlockController
